import React from 'react';

import type { WeeklyPlanningItem } from '../types';

interface WeeklyPlanningProps {
  selectedWeekDate: string;
  onChangeWeekDate: (value: string) => void;
  loading: boolean;
  error: string | null;
  rows: WeeklyPlanningItem[];
}

function WeeklyPlanning({ selectedWeekDate, onChangeWeekDate, loading, error, rows }: WeeklyPlanningProps) {
  const capacityBadgeClass = (total: number, capacity: number): string => {
    const ratio = capacity > 0 ? total / capacity : 0;
    if (ratio <= 0.5) { return 'bg-emerald-600 text-white'; }
    if (ratio <= 0.75) { return 'bg-amber-500 text-white'; }
    return '!bg-rose-600 text-white';
  };

  const dayBadge = (count: number): JSX.Element => {
    if (count <= 0) { return (<span>-</span>); }
    const cls = count <= 1 ? 'bg-emerald-100 text-emerald-800' : count <= 3 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800';
    return (<span className={[cls, 'px-2 py-0.5 rounded'].join(' ')}>{count} RDV</span>);
  };

  const weekRangeLabel = (() => {
    try {
      const d = new Date(selectedWeekDate);
      const monday = new Date(d);
      const day = monday.getDay(); // 0=Sun,1=Mon,..
      const diffToMonday = (day === 0 ? -6 : 1 - day);
      monday.setDate(monday.getDate() + diffToMonday);
      const friday = new Date(monday);
      friday.setDate(monday.getDate() + 4);
      const getISOWeekNumber = (date: Date): number => {
        const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = (tmp.getUTCDay() + 6) % 7;
        tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
        const firstThursday = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
        const diff = tmp.getTime() - firstThursday.getTime();
        return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
      };
      const weekNum = getISOWeekNumber(monday);
      const fmt = (x: Date) => x.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      return `Semaine du ${fmt(monday)} au ${fmt(friday)} (S${weekNum})`;
    } catch {
      return 'Semaine sélectionnée';
    }
  })();

  return (
    <div className="overflow-x-auto rounded-xl border-2 border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="font-bold text-rgpd-blue"><span role="img" aria-label="calendar">📅</span> Semaine en cours</div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">Sélectionner la semaine :</label>
            <input
              type="date"
              value={selectedWeekDate}
              onChange={(e) => onChangeWeekDate(e.target.value)}
              className="rounded-lg border-2 px-2 py-1 text-sm"
              aria-label="Sélection de la semaine"
            />
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="font-semibold text-blue-900"><span role="img" aria-label="calendar">📅</span> {weekRangeLabel}</div>
          <div className="text-sm text-blue-900/80">Vue d'ensemble des disponibilités et de la charge de travail des équipes.</div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
          <div className="inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1"><span role="img" aria-label="house">🏠</span> Transaction</div>
          <div className="inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1"><span role="img" aria-label="key">🔑</span> Location</div>
          <div className="inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1"><span className="inline-block h-3 w-3 rounded bg-emerald-600" /> Faible charge</div>
          <div className="inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1"><span className="inline-block h-3 w-3 rounded bg-amber-500" /> Charge modérée</div>
          <div className="inline-flex items-center gap-2 rounded-md border bg-white px-2 py-1"><span className="inline-block h-3 w-3 rounded !bg-rose-600" /> Charge élevée</div>
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`plsk-${i}`} className="h-10 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700">{error}</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-xl border-2 border-slate-200 bg-white p-8 text-center text-slate-600">
          <div className="mb-2 text-3xl"><span role="img" aria-label="no data">🗂️</span></div>
          <div className="font-semibold">Aucune donnée de planning pour cette semaine</div>
          <div className="text-sm">Les disponibilités s’afficheront ici lorsqu’il y aura des rendez-vous planifiés.</div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="p-2 text-left"><span role="img" aria-label="person">👤</span> Membre</th>
              <th className="p-2">Lun</th>
              <th className="p-2">Mar</th>
              <th className="p-2">Mer</th>
              <th className="p-2">Jeu</th>
              <th className="p-2">Ven</th>
              <th className="p-2"><span role="img" aria-label="chart">📊</span> Charge</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.id}-${row.type}`} className="border-t">
                <td className="p-2 font-semibold" style={{ color: row.type === 'vente' ? '#059669' : '#1d4ed8' }}>
                  <span role="img" aria-label={row.type === 'vente' ? 'house' : 'key'}>{row.type === 'vente' ? '🏠' : '🔑'}</span>{' '}
                  {`${(row.firstname ?? '').toUpperCase()} ${(row.lastname ?? '').toUpperCase()}`.trim() || '—'}
                </td>
                <td className="p-2 text-center">{dayBadge(row.days.mon)}</td>
                <td className="p-2 text-center">{dayBadge(row.days.tue)}</td>
                <td className="p-2 text-center">{dayBadge(row.days.wed)}</td>
                <td className="p-2 text-center">{dayBadge(row.days.thu)}</td>
                <td className="p-2 text-center">{dayBadge(row.days.fri)}</td>
                <td className="p-2 text-center">
                  <span className={[capacityBadgeClass(row.weeklyTotal, row.weeklyCapacity), 'px-2 py-0.5 rounded'].join(' ')}>
                    {row.weeklyTotal}/{row.weeklyCapacity}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default WeeklyPlanning;


